import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { LoadState, useLoad } from "@redotech/react-util/load";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import {
  getCurrentFlow,
  getFlowByID,
} from "@redotech/redo-merchant-app-common/client/team";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import {
  FlowType,
  returnFlowJsonFormat,
  returnTypeToFlowType,
} from "@redotech/redo-model/return-flow";
import { Team } from "@redotech/redo-model/team";
import { modelToSteps } from "@redotech/redo-return-ui/flows/model-to-steps";
import { AI_RETURN_REASONS_STEP_TYPE_NAME } from "@redotech/redo-return-ui/flows/return-flow/ai-return-reasons";
import { REJECT } from "@redotech/redo-return-ui/flows/return-flow/reject";
import {
  getStepResetPointText,
  Step,
} from "@redotech/redo-return-ui/flows/step";
import { alertOnFailure } from "@redotech/redo-web/alert";
import { formatSentenceCase } from "@redotech/util/string";
import { assertNever } from "@redotech/util/type";
import { Node } from "@xyflow/react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { resetReturn } from "../../client/return";
import { NodeData } from "../../setting/flow-editor/components/flow-step-node";
import { modelToStepsDetailsProps } from "../../setting/flow-editor/flow-editor";
import { RejectDetails } from "../../setting/return-flow/reject";
import { reasonTitleForFlowType } from "../../setting/return-flow/util";

export enum Page {
  CONFIRM = "confirm",
  CHANGE_RESET_POINT = "change-reset-point",
}

export enum IconType {
  CHECK_CIRCLE = "check-circle",
  REFRESH_CCW = "refresh-ccw",
}

interface UseResetModalResponse {
  icon: IconType;
  texts: {
    title: string;
    subtitle: string;
    continueButton: string;
    cancelButton: string;
    resetPointLabel: string;
    resetPointText: string;
    resetPointPlaceholder: string;
    resetReasonLabel: string;
    resetReasonPlaceholder: string;
    linkToReturnPageText: string;
    openReturnPageText: string;
  };
  resetReason: string;
  onResetReasonChange: (resetReason: string) => void;
  resetReasonDisabled: boolean;
  onGoToChangeResetPointPage: () => void;
  onGotoChangeResetPointPageDisabled: boolean;
  onContinue: () => void;
  onCancel: () => void;
  continueButtonDisabled: boolean;
  cancelButtonDisabled: boolean;
  page: Page;
  loading: boolean;
  steps: Map<string, Step>;
  flowType: FlowType;
  nodePositionsArray: Array<{ x: number; y: number }> | undefined;
  team: Team | undefined;
  highlightedNodeId: string | undefined;
  currentStepId: string | undefined;
  onResetFlowNodeClick: (event: React.MouseEvent, node: Node<NodeData>) => void;
  flowTypeProps:
    | {
        flowType: "claim" | "return";
        dropdownOpen: boolean;
        setDropdownOpen: (dropdownOpen: boolean) => void;
        items: ("claim" | "return")[];
        setSelectedItem: (item: "claim" | "return") => void;
      }
    | undefined;
  changeDefaultStepSelection: boolean;
  setChangeDefaultStepSelection: (changeDefaultStepSelection: boolean) => void;
  linkToReturnPageLoad: LoadState<string | undefined>;
  openReturnDetails: () => void;
  rejectStepSelected: boolean;
}

export type ResourceResetType =
  | { orderId: string }
  | { returnId: string; returnType: ReturnTypeEnum };

function isReturnId(
  resource: ResourceResetType,
): resource is { returnId: string; returnType: ReturnTypeEnum } {
  return "returnId" in resource;
}

export const flowTypeToText: Record<"claim" | "return", string> = {
  claim: "Claim flow",
  return: "Return flow",
};
export const useResetModal = ({
  resource,
  onSubmitFinished,
  flowPath,
  flowId,
}: {
  resource: ResourceResetType;
  onSubmitFinished: () => void;
  flowPath?: readonly number[];
  flowId?: string;
}): UseResetModalResponse => {
  const team = useContext(TeamContext);
  const user = useContext(UserContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const navigate = useNavigate();

  const [returnTypeDropdownOpen, setReturnTypeDropdownOpen] = useState(false);
  const [returnType, setReturnType] = useState<ReturnTypeEnum>(
    isReturnId(resource) ? resource.returnType : ReturnTypeEnum.RETURN,
  );
  const [page, setPage] = useState<Page>(Page.CONFIRM);
  const [resetReason, setResetReason] = useState<string>("");
  const [resetStepIndex, setResetStepIndex] = useState<number | undefined>(
    undefined,
  );

  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [changeDefaultStepSelection, setChangeDefaultStepSelection] =
    useState<boolean>(false);
  const [finalChangeDefaultStep, setFinalChangeDefaultStep] =
    useState<boolean>(false);

  // If the order has an associated return, the merchant must change the flow on the return page.
  const linkToReturnPageLoad = useLoad(async () => {
    if (isReturnId(resource)) {
      return undefined;
    }

    const getFirstReturnResponse = await rpcClient.getFirstReturnForOrder({
      orderId: resource.orderId,
    });

    if (!getFirstReturnResponse.returnId) {
      return undefined;
    }

    return `/order-management/returns/all-returns/${getFirstReturnResponse.returnId}`;
  }, [resource]);
  const doReset = useHandler(async () => {
    if (!selected) {
      return false;
    }

    return await alertOnFailure("Reseting return failed")(async () => {
      if (isReturnId(resource)) {
        await resetReturn(client, {
          step: +selected,
          changeDefaultStep: finalChangeDefaultStep,
          resetReason,
          returnId: resource.returnId,
          user,
        });
        if (team) {
          amplitude.logEvent("reset-return-support", {
            team: team._id,
            returnId: resource.returnId,
          });
        }
      } else {
        await rpcClient.resetOrderFlow({
          orderId: resource.orderId,
          resetReason,
          returnType,
          step: +selected,
          changeDefaultStep: finalChangeDefaultStep,
          userId: user?._id.toString(),
        });
        if (team) {
          amplitude.logEvent("reset-return-support", {
            team: team._id,
            orderId: resource.orderId,
          });
        }
      }

      onSubmitFinished();

      // For some reason the modal stays open for a moment while navigating,
      // so we wait for 3 seconds before returning to guard against the
      // user being able to submit again.
      await new Promise((resolve) => setTimeout(resolve, 3000));

      return true;
    });
  });

  const resetFlowType = useMemo(
    () => returnTypeToFlowType[returnType],
    [returnType],
  );

  const flowLoad = useLoad(
    async (signal) => {
      const { flow } = flowId
        ? await getFlowByID(client, resetFlowType, flowId, { signal })
        : await getCurrentFlow(client, resetFlowType, true, { signal });
      const returnFlow = returnFlowJsonFormat.read(flow);
      const flowSteps = modelToSteps({
        model: returnFlow,
        firstStepTitle: formatSentenceCase(resetFlowType),
        returnReasonTitle: reasonTitleForFlowType(resetFlowType),
        flowType: resetFlowType,
        ...modelToStepsDetailsProps,
      });

      return {
        steps: flowSteps,
        nodePositions: returnFlow.nodePositions,
        defaultResetStep: returnFlow.defaultResetStep,
      };
    },
    [resetFlowType, flowId],
  );

  const hasSetInitialSelection = useRef(false);

  useEffect(() => {
    if (!flowLoad.value || hasSetInitialSelection.current) {
      return;
    }
    if (resetStepIndex !== undefined) {
      setSelected(resetStepIndex.toString());
      hasSetInitialSelection.current = true;
    } else if (flowLoad.value.defaultResetStep !== undefined) {
      setSelected(String(flowLoad.value.defaultResetStep));
      setResetStepIndex(flowLoad.value.defaultResetStep);
      hasSetInitialSelection.current = true;
    } else {
      const returnReasonTitle = reasonTitleForFlowType(resetFlowType);
      for (const [id, step] of flowLoad.value.steps.entries()) {
        if (
          step.type.title === returnReasonTitle ||
          step.type.title === AI_RETURN_REASONS_STEP_TYPE_NAME
        ) {
          setSelected(id);
          setResetStepIndex(parseInt(id));
          hasSetInitialSelection.current = true;
          break;
        }
      }
    }
  }, [flowLoad.value, resetStepIndex, resetFlowType]);

  const steps: Map<string, Step> = useMemo(() => {
    if (!flowLoad.value) {
      return new Map();
    }
    return flowLoad.value.steps;
  }, [flowLoad.value]);

  const isRejectStep = useHandler((stepId: string | undefined) => {
    if (!stepId) {
      return false;
    }
    const step = steps.get(stepId);
    return !!step && step.type.title === REJECT(RejectDetails).title;
  });

  const onResetFlowNodeClick = useHandler(
    (_event: React.MouseEvent, node: Node<NodeData>) => {
      const step = steps.get(node.id);
      if (!step || isRejectStep(node.id)) {
        return;
      }
      setSelected(node.id);
    },
  );

  const resetPointText = useMemo(() => {
    if (!selected) {
      return "";
    }
    const step = steps.get(selected);

    return step ? getStepResetPointText(step, returnType) : "";
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, steps]);

  let title: string;
  let subtitle: string;
  let continueButton: string;
  let cancelButton: string;
  let icon: IconType;
  let onCancel: () => void;
  let onContinue: () => void;
  let continueButtonDisabled: boolean;
  let cancelButtonDisabled: boolean;

  const hasExistingReturn = linkToReturnPageLoad.value !== undefined;
  switch (page) {
    case Page.CONFIRM:
      icon = IconType.CHECK_CIRCLE;
      title = isReturnId(resource) ? "Confirm Reset" : "Change Return Point";
      subtitle = isReturnId(resource)
        ? "This will delete the current return and void the shipping label. The customer will be able to select the items again in the return portal."
        : "This customer will start the return flow at the reset point.";
      continueButton = "Reset";
      cancelButton = "Change reset point";
      onCancel = () => setPage(Page.CHANGE_RESET_POINT);
      onContinue = async () => {
        await doReset();
      };
      continueButtonDisabled =
        flowLoad.pending ||
        selected === undefined ||
        hasExistingReturn ||
        isRejectStep(selected);
      cancelButtonDisabled = flowLoad.pending || hasExistingReturn;
      break;
    case Page.CHANGE_RESET_POINT:
      icon = IconType.REFRESH_CCW;
      title = isReturnId(resource) ? "Reset return" : "Change Return Point";
      subtitle =
        "Choose the step of the return flow to reset the items to. When returning the items, the customer will start at this step in the return portal.";
      continueButton = "Change reset point";
      cancelButton = "Cancel";
      onCancel = () => {
        setPage(Page.CONFIRM);
        setSelected(resetStepIndex?.toString() ?? undefined);
        setChangeDefaultStepSelection(finalChangeDefaultStep);
      };
      onContinue = () => {
        setPage(Page.CONFIRM);
        let index;
        if (selected === "start") {
          index = 0;
          setSelected("0");
        } else if (selected !== undefined) {
          index = parseInt(selected);
        }
        setResetStepIndex(index);
        setFinalChangeDefaultStep(changeDefaultStepSelection);
      };
      continueButtonDisabled = selected === undefined || isRejectStep(selected);
      cancelButtonDisabled = false;
      break;
    default:
      assertNever(page);
  }

  const openReturnDetails = useHandler(() => {
    if (!linkToReturnPageLoad.value) {
      return;
    }
    navigate(linkToReturnPageLoad.value, { replace: true });
  });

  return {
    icon,
    texts: {
      title,
      subtitle,
      continueButton,
      cancelButton,
      resetPointLabel: "Reset Point",
      resetPointText,
      resetPointPlaceholder: "Select step...",
      resetReasonLabel: "Reset Reason",
      resetReasonPlaceholder: "Enter reason...",
      linkToReturnPageText:
        "This order has an associated return, please click the link below to navigate to the return.",
      openReturnPageText: "Open return page",
    },
    resetReason,
    resetReasonDisabled: hasExistingReturn,
    onResetReasonChange: setResetReason,
    onGoToChangeResetPointPage: () => {
      setPage(Page.CHANGE_RESET_POINT);
    },
    onGotoChangeResetPointPageDisabled: hasExistingReturn,
    onContinue,
    onCancel,
    page,
    continueButtonDisabled,
    cancelButtonDisabled,
    loading: flowLoad.pending,
    flowTypeProps:
      !isReturnId(resource) && team?.settings.packageProtection?.enabled
        ? {
            flowType: returnType === ReturnTypeEnum.RETURN ? "return" : "claim",
            dropdownOpen: returnTypeDropdownOpen,
            setDropdownOpen(dropdownOpen: boolean): void {
              setReturnTypeDropdownOpen(dropdownOpen);
            },
            items: ["claim", "return"],
            setSelectedItem(flowType: "claim" | "return"): void {
              switch (flowType) {
                case "claim":
                  setReturnType(ReturnTypeEnum.CLAIM);
                  break;
                case "return":
                  setReturnType(ReturnTypeEnum.RETURN);
                  break;
                default:
                  assertNever(flowType);
              }
            },
          }
        : undefined,
    steps,
    flowType: resetFlowType,
    nodePositionsArray: flowLoad.value?.nodePositions,
    team,
    highlightedNodeId: selected,
    currentStepId: resetStepIndex?.toString(),
    onResetFlowNodeClick,
    setChangeDefaultStepSelection,
    changeDefaultStepSelection,
    linkToReturnPageLoad,
    openReturnDetails,
    rejectStepSelected: isRejectStep(selected),
  };
};
